import React, { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useDfcThemeMode } from '../../../../context/DfcThemeModeContext';

/** The View Transitions API, where the browser has it (TypeScript 4.7's DOM lib does not). */
type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void> };
};

const REVEAL_MS = 520;

/**
 * Light / dark switching for the village header. Where the browser supports view transitions
 * and the visitor has not asked for reduced motion, the new theme is revealed as a circle that
 * grows out of the switch itself; everywhere else the theme simply changes.
 */
const useDfcThemeSwitch = () => {
  const { mode, setMode } = useDfcThemeMode();
  const isDark = mode === 'dark';

  const toggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const next = isDark ? 'light' : 'dark';
      const doc = document as ViewTransitionDocument;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (!doc.startViewTransition || reduceMotion) {
        setMode(next);
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      // Far enough to cover the farthest corner of the viewport from the switch.
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      const transition = doc.startViewTransition(() => {
        flushSync(() => setMode(next));
      });

      transition.ready
        .then(() => {
          document.documentElement.animate(
            {
              clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`],
            },
            {
              duration: REVEAL_MS,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              pseudoElement: '::view-transition-new(root)',
            } as KeyframeAnimationOptions
          );
        })
        .catch(() => undefined);
    },
    [isDark, setMode]
  );

  return { isDark, toggle };
};

export default useDfcThemeSwitch;
